import { useState, useEffect, ReactNode } from 'react';
import {
  AUTH_EXPIRED_EVENT,
  getAuthToken,
  loginAdmin,
  setAuthToken,
  validateAdminSession,
} from '../api/auth';

interface AuthGuardProps {
  children: ReactNode;
}

const AuthGuard = ({ children }: AuthGuardProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const handleAuthExpired = () => {
      setIsAuthenticated(false);
      setPassword('');
      setError('Your admin session expired. Please log in again.');
    };

    const verifyStoredToken = async () => {
      const jwtToken = getAuthToken();
      if (!jwtToken) {
        setLoading(false);
        return;
      }

      try {
        await validateAdminSession();
        if (!cancelled) {
          setIsAuthenticated(true);
          setError('');
        }
      } catch (err: any) {
        if (!cancelled) {
          setIsAuthenticated(false);
          setError(err.message || 'Your admin session expired. Please log in again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    verifyStoredToken();

    return () => {
      cancelled = true;
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoggingIn(true);
    setError('');
    try {
      const { access_token } = await loginAdmin(password);
      setAuthToken(access_token);
      setIsAuthenticated(true);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('kokki-jwt-token');
    setIsAuthenticated(false);
    setPassword('');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0a0f1e' }}>
        <div style={{ color: '#4ade80', fontSize: '24px' }}>🐸 Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        background: 'linear-gradient(135deg, #0a0f1e 0%, #1a2332 100%)'
      }}>
        <div style={{ 
          background: '#1e293b', 
          padding: '40px', 
          borderRadius: '12px', 
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          width: '100%',
          maxWidth: '400px'
        }}>
          <h1 style={{ color: '#4ade80', marginBottom: '10px', textAlign: 'center' }}>🐸 Kokki Admin</h1>
          <p style={{ color: '#94a3b8', marginBottom: '30px', textAlign: 'center' }}>Analytics Dashboard</p>
          
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#cbd5e1', marginBottom: '8px', fontSize: '14px' }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#0f172a',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  color: '#e2e8f0',
                  fontSize: '16px',
                  outline: 'none'
                }}
                placeholder="Enter admin password"
                autoFocus
              />
            </div>
            
            {error && (
              <div style={{ 
                color: '#ef4444', 
                background: '#7f1d1d', 
                padding: '10px', 
                borderRadius: '6px', 
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loggingIn}
              style={{
                width: '100%',
                padding: '12px',
                background: loggingIn ? '#334155' : '#4ade80',
                color: loggingIn ? '#64748b' : '#0a0f1e',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: loggingIn ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => !loggingIn && (e.currentTarget.style.background = '#22c55e')}
              onMouseOut={(e) => !loggingIn && (e.currentTarget.style.background = '#4ade80')}
            >
              {loggingIn ? '🔄 Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      <button
        onClick={handleLogout}
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '8px 16px',
          background: '#ef4444',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '14px',
          zIndex: 1000
        }}
      >
        Logout
      </button>
    </>
  );
};

export default AuthGuard;
